import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import humanizeString from 'humanize-string'
import {
  Button,
  ButtonBlock,
  ContentBlock,
  DetailItem,
  FieldBlock,
  Font,
  Grid,
  Icon,
  MultiSelectField,
  PageHeader,
  ProgressBar,
  Section,
  SelectField,
  TextArea,
  useToast,
} from '@politechdev/blocks-design-system'
import { CardError, UserSelectField, View } from 'components'
import { Timestamp } from 'components/Time/Timestamp'
import { useRequest } from 'hooks'
import RequestException from 'utils/RequestException'
import { formatDisplayName } from 'utils/formatting'
import { getDateOneYearAgo } from 'utils/dateTime'
import {
  formatForEdit,
  formatForUpdate,
  getPerformanceReview,
  updatePerformanceReview,
} from './utils'
import { PULL_OPTIONS, REVIEW_STATUS } from './constants'
import styles from './PerformanceReviewSingle.module.scss'
import {
  EditablePerformanceReview,
  PerformanceReview,
  PerformanceReviewStatus,
  SubmittablePerformanceReview,
} from './types'
import AssociatedRecordTabs from './PerformanceReviewSingle/AssociatedRecordTabs'

const PerformanceReviewSingle = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const { setToast } = useToast()
  const history = useHistory()
  const { id: idString } = useParams<{ id: string }>()

  const [performanceReview, setPerformanceReview] =
    useState<PerformanceReview | null>(null)

  const [editableFields, setEditableFields] =
    useState<EditablePerformanceReview | null>(null)

  const updateEditableFields = (
    updates: Partial<EditablePerformanceReview>
  ) => {
    setEditableFields(current => ({ ...current!, ...updates }))
  }

  const getPerformanceReviewRequest = useRequest(getPerformanceReview, {
    onSuccess: response => {
      setPerformanceReview(response.performance_review)
      setEditableFields(formatForEdit(response.performance_review))
    },
    onError: e => {
      if (e instanceof RequestException && e.status === 404) {
        history.push(`/not_found?from=${location.pathname}`)
      }
    },
  })

  const updatePerformanceReviewRequest = useRequest(updatePerformanceReview, {
    onSuccess: response => {
      setPerformanceReview(response.performance_review)
      setEditableFields(formatForEdit(response.performance_review))
      setToast({
        message: t('Performance review updated'),
        variant: 'success',
      })
    },
    onError: () => {
      setToast({
        message: t(
          'An issue occured while trying to update this performance review'
        ),
        variant: 'error',
      })
    },
  })

  useEffect(() => {
    void getPerformanceReviewRequest.makeRequest(+idString)
  }, [idString])

  const handleSave = async (fields: SubmittablePerformanceReview) =>
    updatePerformanceReviewRequest.makeRequest(+idString, fields)

  const handleSaveWithTransition = (status: PerformanceReviewStatus) => {
    void handleSave(formatForUpdate({ ...editableFields!, status }))
  }

  if (getPerformanceReviewRequest.hasErrors) {
    return (
      <CardError
        hide={false}
        message={t(
          'An error ocurred while trying to fetch the information for this performance review.'
        )}
      />
    )
  }

  if (
    !getPerformanceReviewRequest.isRequestComplete ||
    !editableFields ||
    !performanceReview
  )
    return <ProgressBar show />

  const reasonOptions =
    performanceReview.turf.options.canvasser_review_reasons.map(v => ({
      label: v,
      value: v,
    }))

  const title = (
    <span>
      {performanceReview.canvasser.full_name} -{' '}
      <Timestamp
        dateTime={performanceReview.created_at}
        formatOptions={{ dateStyle: 'medium' }}
      />
    </span>
  ) as unknown as string

  return (
    <View>
      <PageHeader title={title}>
        <ButtonBlock justify="right">
          <Link to={`${location.pathname}/edit`}>
            <Button.Secondary>
              <Icon.Pencil />
            </Button.Secondary>
          </Link>
          <Link to="/performance_reviews">
            <Button.Secondary>
              <Icon.Xmark />
            </Button.Secondary>
          </Link>
        </ButtonBlock>
      </PageHeader>
      <Grid>
        <div>
          <Section label={t('Review details')}>
            <DetailItem label={t('Created by')}>
              {formatDisplayName(performanceReview.created_by_user)}
            </DetailItem>
            <DetailItem label={t('Requested by')}>
              {formatDisplayName(performanceReview.requested_by_user)}
            </DetailItem>
            {performanceReview.status !== REVIEW_STATUS.OPEN && (
              <DetailItem label={t('Assigned to')}>
                {formatDisplayName(performanceReview.assigned_to_user)}
              </DetailItem>
            )}
            <DetailItem label={t('Opened')}>
              <Timestamp
                dateTime={performanceReview.created_at}
                formatOptions={{ dateStyle: 'medium' }}
              />
            </DetailItem>
            <DetailItem label={t('Status')}>
              {humanizeString(performanceReview.status)}
            </DetailItem>
          </Section>
          <Section>
            {performanceReview.status === REVIEW_STATUS.OPEN && (
              <>
                <FieldBlock>
                  <UserSelectField
                    label={t('Assigned for review')}
                    user={editableFields.assigned_to_user}
                    onSelect={assigned_to_user =>
                      updateEditableFields({ assigned_to_user })
                    }
                  />
                </FieldBlock>
                <FieldBlock variant="large">
                  <MultiSelectField
                    label={t('Reason for review')}
                    values={editableFields.reasons}
                    options={reasonOptions}
                    onSelect={reasons => updateEditableFields({ reasons })}
                    icon={undefined}
                    required
                  />
                </FieldBlock>
                <FieldBlock variant="large">
                  <TextArea
                    label={t('Additional details')}
                    onChange={additional_details =>
                      updateEditableFields({
                        additional_details: additional_details as string,
                      })
                    }
                    value={editableFields.additional_details}
                    required
                  />
                </FieldBlock>
              </>
            )}
            {performanceReview.status !== REVIEW_STATUS.OPEN && (
              <>
                <ContentBlock>
                  <Font.Label>{t('Reasons for review')}</Font.Label>
                  <Font.Copy>{performanceReview.reasons.join(', ')}</Font.Copy>
                </ContentBlock>
                <ContentBlock>
                  <Font.Label>{t('Additional details')}</Font.Label>
                  <Font.Copy>{performanceReview.additional_details}</Font.Copy>
                </ContentBlock>
              </>
            )}
          </Section>
        </div>
        <div>
          <Section label={t('Canvasser under review')}>
            <div>
              <DetailItem label={t('Name')}>
                {performanceReview.canvasser.full_name}
              </DetailItem>
              <DetailItem label={t('Turf')}>
                {performanceReview.turf.name}
              </DetailItem>
              <DetailItem label={t('Shifts*')}>
                {performanceReview.canvasser.rolling_counts.shifts}
              </DetailItem>
              <DetailItem label={t('Forms collected*')}>
                {performanceReview.canvasser.rolling_counts.forms}
              </DetailItem>
            </div>
            <div className={styles.footnote}>
              <Font.Copy variant="hint">
                {t('* Since ')}
                <Timestamp
                  dateTime={getDateOneYearAgo()}
                  formatOptions={{ month: 'short', year: 'numeric' }}
                />
              </Font.Copy>
            </div>
          </Section>
          <Section>
            <FieldBlock>
              <SelectField
                value={editableFields.pull_from_field}
                label={t('Pull canvasser from field')}
                options={PULL_OPTIONS}
                onSelect={pull_from_field =>
                  updateEditableFields({ pull_from_field })
                }
              />
            </FieldBlock>
          </Section>
        </div>
      </Grid>
      <AssociatedRecordTabs
        performanceReview={performanceReview}
        updateReview={handleSave}
      />
      {performanceReview.status === REVIEW_STATUS.OPEN && (
        <ButtonBlock>
          <Button.Accent
            onClick={() => handleSaveWithTransition(REVIEW_STATUS.READY)}
          >
            {t('Submit for review')}
          </Button.Accent>
          <Button.Secondary
            onClick={() => handleSave(formatForUpdate(editableFields))}
          >
            {t('Save performance review')}
          </Button.Secondary>
        </ButtonBlock>
      )}
      {performanceReview.status === REVIEW_STATUS.READY && (
        <ButtonBlock>
          <Button.Accent
            onClick={() => handleSaveWithTransition(REVIEW_STATUS.COMPLETED)}
          >
            {t('Complete review')}
          </Button.Accent>
          <Button.Danger
            onClick={() => handleSaveWithTransition(REVIEW_STATUS.OPEN)}
          >
            {t('Request Changes')}
          </Button.Danger>
          <Button.Secondary
            onClick={() => handleSave(formatForUpdate(editableFields))}
          >
            {t('Save performance review')}
          </Button.Secondary>
        </ButtonBlock>
      )}
    </View>
  )
}

export default PerformanceReviewSingle
